/**
 * UniRankingChips — surface every subject × country leaderboard where
 * this uni cracks the top 20. Anchors the abstract WUA composite to
 * something a family instantly parses: "Ranked #1 for Economics · UK".
 *
 * Each chip links to the canonical ranking page.
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Trophy } from "lucide-react";
import { subjectSlug } from "@/lib/rankingSlug";

const COUNTRY_LABEL = {
  UK: "UK", US: "US", AU: "Australia", CA: "Canada",
  NL: "Netherlands", FR: "France", DE: "Germany", IE: "Ireland",
  IT: "Italy", ES: "Spain", DK: "Denmark", SE: "Sweden", NO: "Norway",
};

export default function UniRankingChips({ uniId, limit = 8 }) {
  const [items, setItems] = useState(null);

  useEffect(() => {
    if (!uniId) return;
    api
      .get(`/rankings/uni-appearances/${uniId}?top_n=20`)
      .then((r) => setItems(r.data.appearances || []))
      .catch(() => setItems([]));
  }, [uniId]);

  if (!items || items.length === 0) return null;

  const topN = items.slice(0, limit);
  const remaining = items.length - topN.length;

  return (
    <div className="mt-4" data-testid="uni-ranking-chips">
      <div className="label-uppercase text-[#4C1D95] mb-2 flex items-center gap-2">
        <Trophy className="h-3.5 w-3.5" />
        Ranked in the WUA leaderboards
      </div>
      <div className="flex flex-wrap gap-2">
        {topN.map((a) => (
          <Link
            key={`${a.country}-${a.subject}`}
            to={`/rankings/${a.country.toLowerCase()}/${subjectSlug(a.subject)}`}
            data-testid={`uni-rank-chip-${a.subject.toLowerCase().replace(/\s+/g, "-")}`}
            className={
              "group inline-flex items-baseline gap-2 px-3 py-1.5 rounded-full border text-[13px] " +
              "transition-colors " +
              (a.rank === 1
                ? "bg-[#FFFBEB] border-amber-300 text-amber-900 hover:border-amber-500"
                : a.rank <= 3
                ? "bg-[#F5F0FF] border-[#4C1D95]/40 text-[#4C1D95] hover:border-[#4C1D95]"
                : "bg-white border-stone-300 text-stone-700 hover:border-[#4C1D95]")
            }
          >
            <span className={
              "font-mono font-bold text-xs " +
              (a.rank === 1
                ? "text-amber-600"
                : a.rank <= 3
                ? "text-[#4C1D95]"
                : "text-stone-500")
            }>
              #{a.rank}
            </span>
            <span>{a.subject}</span>
            <span className="text-[10px] font-mono text-stone-400 group-hover:text-current">
              · {COUNTRY_LABEL[a.country]}
            </span>
          </Link>
        ))}
        {remaining > 0 && (
          <Link
            to={`/rankings/${topN[0].country.toLowerCase()}`}
            data-testid="uni-rank-chip-more"
            className="inline-flex items-center px-3 py-1.5 rounded-full bg-stone-50 border border-stone-200 text-[13px] text-stone-600 hover:border-[#4C1D95]"
          >
            +{remaining} more subject{remaining === 1 ? "" : "s"}
          </Link>
        )}
      </div>
    </div>
  );
}
